import { useEffect, useState } from "react";
import { ArrowRight, Clock, Coins } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { money, dateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, SandboxNotice, StatusBadge } from "@/components/brand";

const History = ({ rows }) => rows.length > 0 && (
  <div className="card-cr mx-auto mt-8 max-w-2xl overflow-x-auto">
    <p className="px-5 pt-4 font-semibold">Withdrawal history</p>
    <table data-testid="withdraw-history-table" className="w-full min-w-[520px] text-sm"><tbody>{rows.map((t) => (
      <tr key={t.id} data-testid={`withdraw-row-${t.id}`} className="border-t border-cr-line">
        <td className="px-5 py-3">{money(t.coins, { whole: true })}</td><td className="px-5 py-3 font-mono text-xs">{t.payout_upi}</td>
        <td className="px-5 py-3"><StatusBadge status={t.status} /></td><td className="px-5 py-3 font-mono text-xs">{t.payout_utr || "-"}</td>
        <td className="px-5 py-3 text-cr-muted">{dateTime(t.created_at)}</td>
      </tr>))}</tbody></table>
  </div>
);

export default function Withdraw() {
  const { wallet, refreshWallet } = useAuth();
  const [coins, setCoins] = useState("");
  const [upi, setUpi] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [rows, setRows] = useState([]);
  const load = () => api.get("/wallet/coin-withdrawals").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const c = Number(coins) || 0;
  const bal = wallet?.coins || 0;
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/wallet/coin-withdrawals", { coins: c, upi_id: upi.trim() }); setDone(data); refreshWallet(); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="withdraw-page">
      <PageTitle crumbs={["Wallet", "Withdraw"]} title="Withdraw Coins" subtitle="Turn coins back into money on your UPI. 1 coin = 1 rupee, paid by your admin." />
      {done ? (
        <div data-testid="withdraw-result" className="card-cr mx-auto max-w-xl p-8 text-center">
          <Clock className="mx-auto h-14 w-14 text-cr-gold" />
          <h2 className="mt-4 text-2xl font-bold">Withdrawal requested</h2>
          <p className="mt-2 text-sm text-cr-ink/75">{done.coins} coins are on hold. Your admin will send ₹{done.coins} to <b>{done.payout_upi}</b>.</p>
          <p data-testid="withdraw-ref" className="mt-3 font-mono text-xs text-cr-muted">{done.reference}</p>
          <button data-testid="withdraw-new-btn" onClick={() => { setDone(null); setCoins(""); }} className="btn-outline mt-6">New request</button>
        </div>
      ) : (
        <div className="card-cr mx-auto max-w-xl p-7">
          <p className="flex items-center gap-2 text-sm">Available coins: <Coins className="h-4 w-4 text-cr-gold" /><b data-testid="withdraw-available" className="text-cr-gold">{bal.toLocaleString("en-IN")}</b></p>
          <label className="label-cr mt-5" htmlFor="wd-coins">Coins to withdraw</label>
          <input id="wd-coins" data-testid="withdraw-amount-input" inputMode="numeric" className="input-cr text-lg" placeholder="Min 100" value={coins} onChange={(e) => setCoins(e.target.value.replace(/\D/g, ""))} />
          <label className="label-cr mt-5" htmlFor="wd-upi">Your UPI ID</label>
          <input id="wd-upi" data-testid="withdraw-details-input" className="input-cr" placeholder="name@upi" value={upi} onChange={(e) => setUpi(e.target.value)} />
          <p className="mt-3 text-sm text-cr-gold">You receive <b data-testid="withdraw-net">{money(c, { whole: true })}</b></p>
          <button data-testid="withdraw-confirm-btn" disabled={busy || c < 100 || c > bal || !upi.includes("@")} onClick={submit} className="btn-gold mt-6 w-full py-3.5 normal-case">{busy ? "Submitting…" : c > bal ? "Not enough coins" : "Request withdrawal"}<ArrowRight className="h-5 w-5" /></button>
        </div>
      )}
      <History rows={rows} />
      <SandboxNotice className="mx-auto mt-6 max-w-2xl" />
    </div>
  );
}
